import { useInfiniteQuery } from '@tanstack/react-query';
import { nanoid } from 'nanoid/non-secure';
import { Dispatch, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createSearchParams, generatePath, useNavigate } from 'react-router';

import { isMusyncConfigured } from '/@/renderer/api/musync/musync-client';
import {
    buildElsewhereSearchParams,
    resolveCommandSearchDestination,
} from '/@/renderer/features/elsewhere/elsewhere-flow';
import { openCreatePlaylistModal } from '/@/renderer/features/playlists/components/create-playlist-form';
import { searchQueries } from '/@/renderer/features/search/api/search-api';
import { Command, CommandPalettePages } from '/@/renderer/features/search/components/command';
import { AppRoute } from '/@/renderer/router/routes';
import { useCurrentServer } from '/@/renderer/store';
import { useDebouncedValue } from '/@/shared/hooks/use-debounced-value';
import { LibraryItem } from '/@/shared/types/domain-types';

interface HomeCommandsProps {
    handleClose: () => void;
    pages: CommandPalettePages[];
    query: string;
    setPages: Dispatch<CommandPalettePages[]>;
    setQuery: Dispatch<string>;
}

export const HomeCommands = ({
    handleClose,
    pages,
    query,
    setPages,
    setQuery,
}: HomeCommandsProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const server = useCurrentServer();
    const [debouncedQuery] = useDebouncedValue(query, 400);
    const searchQuery = debouncedQuery ?? '';
    const searchEnabled = searchQuery.trim().length > 0;
    const albumArtists = useInfiniteQuery(
        searchQueries.searchAlbumArtistsInfinite({
            enabled: searchEnabled,
            searchTerm: searchQuery,
            serverId: server?.id,
        }),
    );
    const albums = useInfiniteQuery(
        searchQueries.searchAlbumsInfinite({
            enabled: searchEnabled,
            searchTerm: searchQuery,
            serverId: server?.id,
        }),
    );
    const songs = useInfiniteQuery(
        searchQueries.searchSongsInfinite({
            enabled: searchEnabled,
            searchTerm: searchQuery,
            serverId: server?.id,
        }),
    );
    const destination = resolveCommandSearchDestination({
        albums: albums.data?.pages.flatMap((page) => page.albums).length ?? 0,
        artists: albumArtists.data?.pages.flatMap((page) => page.albumArtists).length ?? 0,
        musyncConfigured: isMusyncConfigured(),
        settled: searchEnabled && albumArtists.isFetched && albums.isFetched && songs.isFetched,
        songs: songs.data?.pages.flatMap((page) => page.songs).length ?? 0,
    });

    const handleCreatePlaylistModal = useCallback(() => {
        handleClose();
        openCreatePlaylistModal(server);
    }, [handleClose, server]);

    /*
     * Going to the peer network is an intent of its own, so it gets its own
     * command instead of being a mode the library result count happens to
     * select. It carries no `source`, which keeps it on the scope that resolves
     * an artist identity — see `elsewhere-route.tsx`'s `shouldResolveArtist`.
     */
    const handleElsewhereSearch = () => {
        navigate(
            {
                pathname: AppRoute.ELSEWHERE,
                search: buildElsewhereSearchParams(query),
            },
            { state: { navigationId: nanoid() } },
        );
        handleClose();
        setQuery('');
    };

    const handleLibrarySearch = () => {
        if (destination === 'loading') return;
        const itemType =
            destination === 'albums'
                ? LibraryItem.ALBUM
                : destination === 'artists'
                  ? LibraryItem.ALBUM_ARTIST
                  : LibraryItem.SONG;
        navigate(
            {
                pathname: generatePath(AppRoute.SEARCH, { itemType }),
                search: createSearchParams({
                    query,
                }).toString(),
            },
            {
                state: {
                    navigationId: nanoid(),
                },
            },
        );
        handleClose();
        setQuery('');
    };

    const librarySearchCommand = (
        <Command.Item
            disabled={Boolean(query) && destination === 'loading'}
            key="search-library"
            onSelect={handleLibrarySearch}
            value={t('common.search')}
        >
            {!query
                ? `${t('common.search')}...`
                : destination === 'loading'
                  ? t('common.searching', { defaultValue: 'Searching...' })
                  : t('page.globalSearch.commands.searchFor', { query })}
        </Command.Item>
    );

    // Always offered, never hidden by how much of the library happens to match:
    // wanting the peer network does not depend on what you already own.
    const elsewhereSearchCommand =
        query && isMusyncConfigured() ? (
            <Command.Item
                key="search-elsewhere"
                onSelect={handleElsewhereSearch}
                value={t('page.elsewhere.tabLabel', { defaultValue: 'Search elsewhere' })}
            >
                {t('page.globalSearch.commands.searchSoulseekFor', {
                    defaultValue: `Search Soulseek for "${query}"`,
                    query,
                })}
            </Command.Item>
        ) : null;

    return (
        <>
            <Command.Group heading={t('page.globalSearch.title')}>
                {destination === 'elsewhere'
                    ? [elsewhereSearchCommand, librarySearchCommand]
                    : [librarySearchCommand, elsewhereSearchCommand]}
                <Command.Item onSelect={handleCreatePlaylistModal}>
                    {t('action.createPlaylist')}...
                </Command.Item>
                <Command.Item onSelect={() => setPages([...pages, CommandPalettePages.GO_TO])}>
                    {t('page.globalSearch.commands.goToPage')}...
                </Command.Item>
                <Command.Item
                    onSelect={() => setPages([...pages, CommandPalettePages.MANAGE_SERVERS])}
                >
                    {t('page.globalSearch.commands.serverCommands')}
                    ...
                </Command.Item>
            </Command.Group>
        </>
    );
};
